import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultRoleResource,
    RoleMutationResourceSchema,
    RolePropertyValidation,
    useRoleActions,
} from '../../../../../resource/resources/artifacts/role/roleResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { RoleContent } from './RoleContent';

const RoleOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const roleActions = useRoleActions();
    const roleQuery = roleActions.useGetResource(id);
    const updateRole = roleActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: RoleMutationResourceSchema,
        defaultValues: defaultRoleResource,
        submitHandler: async (data) => {
            await updateRole.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: roleQuery.data,
    });

    return (
        <QueryBaseComponent name="Role" query={roleQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <RoleContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const RoleOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const roleActions = useRoleActions();

    const parametersQuery = roleActions.useGetAllResources(parentId);

    const resourceType = ResourceType.ROLE;

    return (
        <QueryBaseComponent name="Roles" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Roles"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <RoleOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: RolePropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
